function compact(value: number, fractionDigits: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(fractionDigits)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(fractionDigits)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(fractionDigits)}K`;
  return value.toFixed(fractionDigits);
}

export function formatWhole(value: number | null): string {
  return value === null ? '-' : value.toLocaleString();
}

export function formatMonthShort(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
}

export function formatShortDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** First two characters of `primary`, or of `fallback` when `primary` is blank, uppercased — or
 * `null` when both are blank, so a caller can render nothing (reserving the same layout space)
 * instead of a placeholder glyph. */
export function initialsFrom(
  primary: string | null | undefined,
  fallback?: string | null,
): string | null {
  const source = primary?.trim() || fallback?.trim() || '';
  return source === '' ? null : source.slice(0, 2).toUpperCase();
}

export function formatCompactCurrency(
  value: number | null,
  symbol: string,
  signed = false,
): string {
  if (value === null) return '-';
  const magnitude = Math.abs(value);
  const sign = value < 0 ? '-' : signed ? '+' : '';
  const fractionDigits = magnitude >= 1_000 ? 1 : 2;
  return `${sign}${symbol}${compact(magnitude, fractionDigits)}`;
}

export function formatCount(value: number | null, fractionDigits = 1): string {
  if (value === null) return '-';
  return Math.abs(value) >= 1_000 ? compact(value, fractionDigits) : value.toLocaleString();
}

export function formatRatio(value: number | null): string {
  if (value === null) return '-';
  return value.toFixed(2);
}

export function formatPercent(fraction: number | null, fractionDigits = 1): string {
  if (fraction === null) return '-';
  return `${(fraction * 100).toFixed(fractionDigits)}%`;
}

export function deltaPercentPoints(fraction: number | null): number | null {
  return fraction === null ? null : Math.round(fraction * 100);
}

// Stat cards omit the delta chip entirely when there is no comparison, so return an
// object that either carries deltaPercent or nothing (exactOptionalPropertyTypes).
export function deltaCardProp(fraction: number | null): { deltaPercent?: number } {
  const points = deltaPercentPoints(fraction);
  return points === null ? {} : { deltaPercent: points };
}
